/**
 * Renderer, night lighting, and atmosphere.
 *
 * The core trick: fog is both the aesthetic and the performance budget. Dense
 * exponential fog means almost nothing is visible beyond ~60 m, which is
 * exactly the horror we want *and* the reason we can draw a forest of nine
 * thousand trees on modest hardware. Every visual decision here leans into
 * that rather than fighting it.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ATMOSPHERE } from '@static-pines/shared';

/** Film grain, vignette, desaturation and a chase-time red pulse. */
const AtmosphereShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uVignette: { value: 0.82 },
    uGrain: { value: 0.042 },
    /** 0..1 — drives the red pulse and tunnel-vision when the monster is close. */
    uDread: { value: 0 },
    /** 0..1 — full-screen fade for the death sequence. */
    uFade: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uVignette;
    uniform float uGrain;
    uniform float uDread;
    uniform float uFade;
    varying vec2 vUv;

    // A sin-based hash at high UV scale beats against the pixel grid and
    // produces visible diagonal banding rather than grain. This integer-style
    // hash stays uncorrelated at every scale.
    float hash(vec2 p) {
      vec3 v = fract(vec3(p.xyx) * 0.1031);
      v += dot(v, v.yzx + 33.33);
      return fract((v.x + v.y) * v.z);
    }

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);

      // Vignette, tightening as dread rises into tunnel vision.
      vec2 centered = vUv - 0.5;
      float dist = length(centered);
      float vig = smoothstep(0.85, 0.25, dist * (uVignette + uDread * 0.85));
      color.rgb *= vig;

      // Slight desaturation: moonlight has almost no colour information.
      float luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));
      color.rgb = mix(vec3(luma), color.rgb, 0.72);

      // A sick red wash when he is near.
      color.rgb = mix(color.rgb, vec3(luma * 1.3, luma * 0.28, luma * 0.3), uDread * 0.55);

      // Animated grain. Kept near-uniform: weighting it toward the shadows
      // made it dominate a game that is mostly shadow.
      float grain = hash(vUv * vec2(1920.0, 1080.0) + fract(uTime) * 977.0) - 0.5;
      color.rgb += grain * uGrain * (0.55 + 0.45 * luma);

      color.rgb *= (1.0 - uFade);
      gl_FragColor = color;
    }
  `,
};

export class SceneRenderer {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;

  private readonly composer: EffectComposer;
  private readonly atmospherePass: ShaderPass;

  readonly moon: THREE.DirectionalLight;
  readonly ambient: THREE.HemisphereLight;

  private time = 0;

  constructor(canvasParent: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: false, // grain hides aliasing; the fill rate is better spent elsewhere
      powerPreference: 'high-performance',
      // Lets a frame be read back after rendering, for dev screenshots.
      preserveDrawingBuffer: import.meta.env.DEV,
    });
    /**
     * Phone GPUs meet phone DPRs: a 3× screen would mean nine times the
     * fragments of a 1× one, spent on a game that is mostly fog and darkness.
     * Film grain already hides the softness, so mobile renders at 1×.
     */
    const coarsePointer = matchMedia('(pointer: coarse)').matches;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, coarsePointer ? 1 : 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Composer runs several passes per frame; without this the stats would
    // only ever describe the last one.
    this.renderer.info.autoReset = false;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = ATMOSPHERE.TONE_EXPOSURE;
    canvasParent.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      72,
      window.innerWidth / window.innerHeight,
      0.1,
      ATMOSPHERE.DRAW_DISTANCE,
    );

    // Fog colour doubles as the clear colour so the horizon dissolves rather
    // than ending in a visible edge. It is deliberately *not* pure black: a
    // faintly blue fog is what lets distant trunks read as silhouettes instead
    // of disappearing into a void.
    const fogColor = new THREE.Color(0x0c131c);
    this.scene.fog = new THREE.FogExp2(fogColor, ATMOSPHERE.FOG_DENSITY);
    this.scene.background = fogColor;

    this.ambient = new THREE.HemisphereLight(0x2a3550, 0x0a0c08, ATMOSPHERE.AMBIENT_INTENSITY);
    this.scene.add(this.ambient);

    this.moon = new THREE.DirectionalLight(0x8fa6d8, ATMOSPHERE.MOON_INTENSITY);
    this.moon.position.set(-60, 90, 40);
    this.moon.castShadow = false; // the flashlight carries the shadow budget
    this.scene.add(this.moon);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.atmospherePass = new ShaderPass(AtmosphereShader);
    this.composer.addPass(this.atmospherePass);

    /**
     * OutputPass must terminate the chain.
     *
     * Intermediate passes work in linear space. Writing that buffer straight
     * to the screen — as a custom ShaderPass with renderToScreen does — skips
     * tone mapping and the linear-to-sRGB conversion, which crushes every
     * midtone while leaving highlights intact. In a game this dark that reads
     * as "the lighting is broken", and it cost a while to diagnose as anything
     * other than lights being too dim.
     */
    this.composer.addPass(new OutputPass());

    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => this.onResize();

  /**
   * Release everything this renderer owns.
   *
   * Only its own canvas is removed — the renderer shares its parent element
   * with the menu and HUD, so clearing the container would take those with it.
   */
  dispose(): void {
    window.removeEventListener('resize', this.handleResize);

    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      mesh.geometry?.dispose?.();

      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(material)) {
        for (const m of material) m.dispose();
      } else {
        material?.dispose?.();
      }
    });
    this.scene.clear();

    this.composer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
  }

  /** 0..1 — how close and how aware the monster is. Drives the post effects. */
  setDread(value: number): void {
    this.atmospherePass.uniforms.uDread!.value = value;
  }

  /** 0..1 — screen fade, used by the death sequence. */
  setFade(value: number): void {
    this.atmospherePass.uniforms.uFade!.value = value;
  }

  render(dt: number): void {
    this.time += dt;
    this.atmospherePass.uniforms.uTime!.value = this.time;
    this.renderer.info.reset();
    this.composer.render();
  }

  get domElement(): HTMLCanvasElement {
    return this.renderer.domElement;
  }
}
